import clsx from 'clsx';
import { useDebounce } from 'usehooks-ts';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';

import Collapse from 'components/collapse';
import TextInput from 'components/text-input';
import BaseScreen from 'components/base-screen';
import { ButtonProps } from 'components/button';

import CheckboxList from 'components/checkbox-list';
import InputsWithRangeSlider, {
  getPlaceholdersForInputsWithRangeSlider,
} from 'components/inputs-with-range-slider';

import useBoundStore from 'store';
import { RangeNumber } from 'lib/types';
import { formatArea, removeZeroRoomApartments, toReadablePrice } from 'lib/utils';
import useApartmentsFilterLimits from 'api/hooks/use-apartments-filter-limits';
import useViewApartmentDealsButtonProps from 'hooks/use-view-apartment-deals-button-props';

import magnifier from 'assets/images/magnifier.svg';
import styles from './styles.module.scss';
import PolygonFilterData from 'components/items-ymap/Polygon/PolygonFilterData';
import { MarketTypes } from 'api/types';
import { IPolygonItemRequest } from 'api/hooks/use-apartments-limits-secondary';
import { SQ_METER_SIGN } from 'lib/constants';
import { ApartmentFilters } from 'api/hooks/use-aparments-filter-query';
import Loader from 'components/loader';

const BuyApartmentFilters: React.FC = () => {
  const navigate = useNavigate();

  const { filterLimits, isLoading } = useApartmentsFilterLimits({
    marketType: MarketTypes.PRIMARY,
  } as ApartmentFilters);

  const storePriceRange = useBoundStore((s) => s.priceRange);
  const storePriceSqMRange = useBoundStore((s) => s.pricePerSqMRange);
  const storeMarketPriceRange = useBoundStore((s) => s.marketPriceRange);
  const storeSquareFootageRange = useBoundStore((s) => s.areaSqFootageRange);
  const storeDiffWithMarketPrice = useBoundStore((s) => s.diffWithMarketPrice);

  const setFilters = useBoundStore((s) => s.setFilters);

  const selectedRoomTypes = useBoundStore((s) => s.selectedRoomTypes);
  const toggleSelectedRoomTypes = useBoundStore((s) => s.toggleSelectedRoomTypes);
  const clearRoomTypes = useBoundStore((s) => s.clearRoomTypes);

  const [search, setSearch] = useState('');

  const [marketPriceDiff, setMarketPriceDiff] = useState(storeDiffWithMarketPrice);

  const [priceRange, setPriceRange] = useState<[number?, number?]>([
    storePriceRange.min,
    storePriceRange.max,
  ]);

  const [marketPriceRange, setMarketPriceRange] = useState<[number?, number?]>([
    storeMarketPriceRange.min,
    storeMarketPriceRange.max,
  ]);

  const [squareFootageRange, setSquareFootageRange] = useState<[number?, number?]>([
    storeSquareFootageRange.min,
    storeSquareFootageRange.max,
  ]);

  const [pricePerSqmRange, setPriceRangePerSqm] = useState<[number?, number?]>([
    storePriceSqMRange.min,
    storePriceSqMRange.max,
  ]);

  const buttons: ButtonProps[] = [
    {
      ...useViewApartmentDealsButtonProps(),
      variant: 'default',
      onClick: () => navigate(-1),
    },
  ];

  const filterParams = useMemo(
    () => ({
      priceRange,
      squareFootageRange,
      marketPriceDiff,
      selectedRoomTypes,
      pricePerSqmRange,
    }),
    [priceRange, squareFootageRange, marketPriceDiff, selectedRoomTypes, pricePerSqmRange],
  );
  const debouncedFilterParams = useDebounce(filterParams);

  useEffect(() => {
    const priceRange: RangeNumber = {};
    if (debouncedFilterParams.priceRange[0]) {
      priceRange.min = debouncedFilterParams.priceRange[0];
    }
    if (debouncedFilterParams.priceRange[1]) {
      priceRange.max = debouncedFilterParams.priceRange[1];
    }

    const areaSqFootageRange: RangeNumber = {};
    if (debouncedFilterParams.squareFootageRange[0]) {
      areaSqFootageRange.min = debouncedFilterParams.squareFootageRange[0];
    }
    if (debouncedFilterParams.squareFootageRange[1]) {
      areaSqFootageRange.max = debouncedFilterParams.squareFootageRange[1];
    }

    let diffWithMarketPrice: number | undefined;
    if (debouncedFilterParams.marketPriceDiff) {
      diffWithMarketPrice = debouncedFilterParams.marketPriceDiff;
    }

    let selectedRoomTypes: Set<number> = new Set();
    if (debouncedFilterParams.selectedRoomTypes) {
      selectedRoomTypes = debouncedFilterParams.selectedRoomTypes;
    }

    let priceRangePerSqm: RangeNumber = {};

    if (debouncedFilterParams.pricePerSqmRange[0]) {
      priceRangePerSqm.min = debouncedFilterParams.pricePerSqmRange[0];
    }
    if (debouncedFilterParams.pricePerSqmRange[1]) {
      priceRangePerSqm.max = debouncedFilterParams.pricePerSqmRange[1];
    }

    setFilters({
      priceRange,
      areaSqFootageRange,
      diffWithMarketPrice,
      selectedRoomTypes,
      pricePerSqMRange: priceRangePerSqm,
    });
  }, [setFilters, debouncedFilterParams]);

  const searchMatch = (str: string) => {
    return search !== '' && !str.toLowerCase().includes(search.toLowerCase());
  };

  const getCollapseProps = (header: string) => ({
    header,
    className: clsx(styles.filterItem, searchMatch(header) && styles.hidden),
  });

  const transformedFirstlyTypes = filterLimits?.facilityTypes?.length
    ? removeZeroRoomApartments(filterLimits?.facilityTypes)?.map((i) => ({
        id: i.id,
        key: i.id,
        label: i.fullTitle,
      }))
    : [];

  const selectedPolygon = useBoundStore((s) => s.selectedPolygon);
  const setSelectedPolygon = useBoundStore((s) => s.setSelectedPolygon);

  const handleClearPolygon = () => {
    setSelectedPolygon(null);
  };

  return (
    <BaseScreen
      padContent
      title='Фильтры'
      buttons={buttons}
      footerClassName={styles.footer}
      floatingButtons={false}
    >
      {isLoading ? (
        <div className={styles.loader}>
          <Loader />
        </div>
      ) : (
        <div className={styles.container}>
          <TextInput
            tall
            value={search}
            icon={magnifier}
            onChange={setSearch}
            className={styles.search}
            placeholder='Поиск по фильтрам'
          />

          {filterLimits && (
            <Collapse {...getCollapseProps('Количество комнат')}>
              <CheckboxList
                items={transformedFirstlyTypes}
                selected={selectedRoomTypes}
                onChange={toggleSelectedRoomTypes}
                onClear={clearRoomTypes}
              />
            </Collapse>
          )}

          <Collapse {...getCollapseProps('Цена')}>
            <InputsWithRangeSlider
              withSlider={false}
              value={priceRange}
              onChange={setPriceRange}
              inputVariant='price'
              min={filterLimits?.priceMin ?? 0}
              max={filterLimits?.priceMax ?? 0}
              step={1_000}
              placeholders={getPlaceholdersForInputsWithRangeSlider(
                filterLimits?.priceMin,
                filterLimits?.priceMax,
                toReadablePrice,
              )}
            />
          </Collapse>

          <Collapse {...getCollapseProps(`Цена за 1 ${SQ_METER_SIGN}`)}>
            <InputsWithRangeSlider
              withSlider={false}
              value={pricePerSqmRange}
              onChange={setPriceRangePerSqm}
              inputVariant='priceSqM'
              min={filterLimits?.pricePerSqMMin ?? 0}
              max={filterLimits?.pricePerSqMMax ?? 100000000}
              step={1_000}
              placeholders={getPlaceholdersForInputsWithRangeSlider(
                filterLimits?.pricePerSqMMin,
                filterLimits?.pricePerSqMMax,
                toReadablePrice,
              )}
            />
          </Collapse>

          <Collapse {...getCollapseProps('Площадь')}>
            <InputsWithRangeSlider
              withSlider={false}
              value={squareFootageRange}
              onChange={setSquareFootageRange}
              inputVariant='area'
              min={filterLimits?.areaMin ?? 0}
              max={filterLimits?.areaMax ?? 0}
              placeholders={getPlaceholdersForInputsWithRangeSlider(
                filterLimits?.areaMin,
                filterLimits?.areaMax,
                formatArea,
              )}
            />
          </Collapse>

          <Collapse {...getCollapseProps('Карта')}>
            <PolygonFilterData
              type={MarketTypes.PRIMARY}
              onClearPolygon={handleClearPolygon}
              selectedPolygonCount={selectedPolygon?.length ?? 0}
              polygons={(filterLimits?.polygons as IPolygonItemRequest[]) || []}
            />
          </Collapse>
        </div>
      )}
    </BaseScreen>
  );
};

export default BuyApartmentFilters;
